import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { getRegister, RegisterEntry } from '../services/apiService';
import { formatBRL } from '../utils/FormatDecimal';
import {
  ChevronDown,
  ChevronRight,
  X,
  CheckCircle2,
  Clock,
  FileText,
  Wallet,
} from 'lucide-react';
import {
  useContasReceberData,
  RecebivelCliente,
  RecebivelCompetencia,
} from '../hooks/useContasReceberData';

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------

function StatusBadge({ status }: { status: 'pendente' | 'recebido' }) {
  if (status === 'recebido') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
        <CheckCircle2 className="h-3 w-3" /> Recebido
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
      <Clock className="h-3 w-3" /> Pendente
    </span>
  );
}

// ---------------------------------------------------------------------------
// KPI mini card
// ---------------------------------------------------------------------------

function KpiMini({ label, value, color }: { label: string; value: string; color: string }) {
  const colorClasses: Record<string, string> = {
    blue: 'border-l-blue-500 text-blue-700',
    green: 'border-l-green-500 text-green-700',
    orange: 'border-l-orange-500 text-orange-700',
  };
  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 border-l-4 ${colorClasses[color] || colorClasses.blue} p-3 sm:p-4`}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-lg sm:text-xl font-bold">{value}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section header
// ---------------------------------------------------------------------------

function SectionHeader({
  icon,
  title,
  total,
  expanded,
  onToggle,
}: {
  icon: React.ReactNode;
  title: string;
  total: number;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between py-2 hover:bg-gray-50 -mx-2 px-2 rounded"
    >
      <div className="flex items-center gap-2">
        {expanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
        <span className="text-gray-500">{icon}</span>
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">{title}</h3>
      </div>
      {Math.abs(total) >= 0.01 && (
        <span className="text-sm font-bold text-blue-700">
          {formatBRL(Math.abs(total))}
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Client card
// ---------------------------------------------------------------------------

function ClienteCard({
  cliente,
  onDrilldown,
}: {
  cliente: RecebivelCliente;
  onDrilldown: (account: string, label: string) => void;
}) {
  const hasBalance = Math.abs(cliente.totalBalance) >= 0.01;
  const [expanded, setExpanded] = useState(hasBalance);
  const hasCompetencias = cliente.competencias.length > 0;

  return (
    <div className="border border-gray-100 rounded-lg">
      <button
        onClick={() => hasCompetencias ? setExpanded((p) => !p) : onDrilldown(cliente.full_path, cliente.cliente)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 rounded-lg"
      >
        <div className="flex items-center gap-3">
          {hasCompetencias ? (
            expanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />
          ) : (
            <span className="w-4" />
          )}
          <span className="text-sm font-semibold text-gray-800">{cliente.cliente}</span>
        </div>
        {hasBalance ? (
          <span className="text-sm font-bold tabular-nums text-blue-700">
            {formatBRL(Math.abs(cliente.totalBalance))}
          </span>
        ) : (
          <span className="text-xs text-gray-500 font-medium">Tudo recebido</span>
        )}
      </button>

      {expanded && hasCompetencias && (
        <div className="px-4 pb-3 space-y-1">
          {cliente.competencias.map((comp) => (
            <CompetenciaLine
              key={comp.competencia}
              cliente={cliente.cliente}
              comp={comp}
              onDrilldown={onDrilldown}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Competência line within a client
// ---------------------------------------------------------------------------

function CompetenciaLine({
  cliente,
  comp,
  onDrilldown,
}: {
  cliente: string;
  comp: RecebivelCompetencia;
  onDrilldown: (account: string, label: string) => void;
}) {
  const isZero = comp.status === 'recebido';

  return (
    <div
      className={`flex items-center justify-between py-1.5 px-2 rounded text-sm cursor-pointer hover:bg-blue-50 ${
        isZero ? 'opacity-60' : ''
      }`}
      onClick={() => onDrilldown(comp.full_path, `${cliente} (${comp.competencia})`)}
    >
      <div className="flex items-center gap-3">
        <span className="font-mono text-xs text-gray-600">{comp.competencia}</span>
        <StatusBadge status={comp.status} />
      </div>
      {!isZero && (
        <span className="text-sm tabular-nums font-medium text-blue-700">
          {formatBRL(Math.abs(comp.balance))}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function ContasReceberPage() {
  const { data, loading, error } = useContasReceberData();

  // Section expansion
  const [showAFaturar, setShowAFaturar] = useState(true);
  const [showContasReceber, setShowContasReceber] = useState(true);

  // Drilldown modal
  const [drilldown, setDrilldown] = useState<{ account: string; label: string } | null>(null);
  const [drilldownEntries, setDrilldownEntries] = useState<RegisterEntry[]>([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  const openDrilldown = async (account: string, label: string) => {
    setDrilldown({ account, label });
    setDrilldownLoading(true);
    try {
      const result = await getRegister({ account, limit: 50 });
      setDrilldownEntries(result.entries);
    } catch {
      setDrilldownEntries([]);
    } finally {
      setDrilldownLoading(false);
    }
  };

  if (loading) return <LoadingSpinner message="Carregando contas a receber..." />;
  if (error) return <EmptyState title="Erro" description={error} />;
  if (!data) return null;

  const { aFaturar, contasReceber, totals } = data;

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <KpiMini label="Total a Receber" value={formatBRL(Math.abs(totals.grandTotal))} color="blue" />
        <KpiMini label="A Faturar" value={formatBRL(Math.abs(totals.aFaturar))} color="orange" />
        <KpiMini label="Contas a Receber" value={formatBRL(Math.abs(totals.contasReceber))} color="green" />
      </div>

      {/* A Faturar — work done, no invoice yet */}
      {aFaturar.length > 0 && (
        <Card>
          <CardContent>
            <SectionHeader
              icon={<FileText className="h-4 w-4" />}
              title="A Faturar"
              total={totals.aFaturar}
              expanded={showAFaturar}
              onToggle={() => setShowAFaturar((p) => !p)}
            />
            {showAFaturar && (
              <div className="mt-3 space-y-2">
                <p className="text-xs text-gray-500 mb-2">
                  Trabalho realizado, aguardando emissão de nota fiscal.
                </p>
                {aFaturar.map((cliente) => (
                  <ClienteCard key={cliente.full_path} cliente={cliente} onDrilldown={openDrilldown} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Contas a Receber — invoice issued, awaiting payment */}
      {contasReceber.length > 0 && (
        <Card>
          <CardContent>
            <SectionHeader
              icon={<Wallet className="h-4 w-4" />}
              title="Contas a Receber"
              total={totals.contasReceber}
              expanded={showContasReceber}
              onToggle={() => setShowContasReceber((p) => !p)}
            />
            {showContasReceber && (
              <div className="mt-3 space-y-2">
                <p className="text-xs text-gray-500 mb-2">
                  Nota fiscal emitida, aguardando pagamento.
                </p>
                {contasReceber.map((cliente) => (
                  <ClienteCard key={cliente.full_path} cliente={cliente} onDrilldown={openDrilldown} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {aFaturar.length === 0 && contasReceber.length === 0 && (
        <EmptyState title="Nenhum valor a receber" description="Não há contas a receber ou trabalho a faturar registrado." />
      )}

      {/* Drilldown Modal */}
      {drilldown && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-gray-800">{drilldown.label}</h3>
              <button onClick={() => setDrilldown(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-auto flex-1 p-4">
              {drilldownLoading ? (
                <LoadingSpinner message="Carregando lançamentos..." />
              ) : drilldownEntries.length === 0 ? (
                <p className="text-gray-500 text-sm">Nenhum lançamento encontrado.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-500 text-xs">
                      <th className="text-left py-2 px-2">Data</th>
                      <th className="text-left py-2 px-2">Descrição</th>
                      <th className="text-right py-2 px-2">Valor</th>
                      <th className="text-right py-2 px-2">Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drilldownEntries.map((entry, i) => (
                      <tr key={i} className="border-b border-gray-50">
                        <td className="py-1.5 px-2 text-gray-600 whitespace-nowrap">{entry.date}</td>
                        <td className="py-1.5 px-2 text-gray-800 truncate max-w-xs">{entry.payee}</td>
                        <td className={`py-1.5 px-2 text-right tabular-nums whitespace-nowrap ${
                          entry.amount < 0 ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {entry.amount < 0 ? '-' : '+'}{formatBRL(Math.abs(entry.amount))}
                        </td>
                        <td className="py-1.5 px-2 text-right tabular-nums text-gray-500 whitespace-nowrap">
                          {formatBRL(Math.abs(entry.running_balance?.BRL || 0))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
